import _ from 'lodash';

const READONLY_PARAMS_PREFIX = '__readonly__.';
const ENV_PARAMS_PREFIX = '__env__-';

/**
 * Serializes an environment parameter key by adding the '__env__-' prefix
 * and formatting it as '__env__-<env>-<key>'.
 *
 * @param env - The environment name (e.g., 'production', 'qa').
 * @param key - The parameter key within the environment.
 * @returns The serialized key string.
 *
 * @example
 * serializeEnvironmentParamKey('qa', 'clusterSize');
 * // Returns '__env__-qa-clusterSize'
 */
export function serializeEnvironmentParamKey(env: string, key: string): string {
  return `${ENV_PARAMS_PREFIX}${env}-${key}`;
}

/**
 * Checks if a key is a serialized environment parameter key.
 *
 * @param key - The key to check.
 * @returns True if the key is a serialized environment parameter key, false otherwise.
 *
 * @example
 * isEnvironmentParamKey('__env__-qa-clusterSize');
 * // Returns true
 */
export function isEnvironmentParamKey(key: string): boolean {
  return key.startsWith(ENV_PARAMS_PREFIX);
}

/**
 * Deserializes an environment parameter key by removing the '__env__-' prefix
 * and splitting it into its environment and parameter key components.
 *
 * @param serializedKey - The serialized key string in the format '__env__-<env>-<key>'.
 * @returns A tuple containing the environment name and the parameter key.
 *
 * @example
 * deserializeEnvironmentParamKey('__env__-qa-clusterSize');
 * // Returns { env: 'qa', key: 'clusterSize' }
 */
export function deserializeEnvironmentParamKey(serializedKey: string): {
  env: string;
  key: string;
} {
  const parts = serializedKey.replace(ENV_PARAMS_PREFIX, '').split('-');
  return { env: parts.at(0) ?? '', key: parts.slice(1).join('-') };
}

/**
 * Serializes a readonly parameter key by adding the '__readonly__.' prefix.
 *
 * @param key - The parameter key.
 * @returns The serialized readonly key string.
 *
 * @example
 * serializeReadonlyParamKey('summary');
 * // Returns '__readonly__.summary'
 */
export function serializeReadonlyParamKey(key: string): string {
  return `${READONLY_PARAMS_PREFIX}${key}`;
}

/**
 * Extracts environment-specific parameters from a given parameters object by identifying keys
 * prefixed with '__env__-', and groups them by environment.
 *
 * If `inPlace` is true, the original object is modified by removing the extracted keys.
 * If `inPlace` is false (default), a deep clone is created and modified instead.
 *
 * @param parameters - The original parameters object containing key-value pairs.
 * @param inPlace - Whether to mutate the original object (true) or work on a clone (false).
 * @returns An object with the cleaned parameters and an optional environmentParameters object
 *          containing the grouped environment-specific parameters.
 *
 * @example
 * extractEnvironmentParameters({
 *   '__env__-qa-timeout': 5000,
 *   'retryCount': 3
 * }, false);
 * // Returns:
 * // {
 * //   parameters: { retryCount: 3 },
 * //   environmentParameters: { qa: { timeout: 5000 } }
 * // }
 */
export function extractEnvironmentParameters({
  parameters,
  inPlace = false,
}: {
  parameters: Record<string, any>;
  inPlace?: boolean;
}): {
  parameters: Record<string, any>;
  environmentParameters?: Record<string, Record<string, any>>;
} {
  const environmentParameters: Record<string, Record<string, any>> = {};
  const outputParameters = inPlace ? parameters : _.cloneDeep(parameters);

  Object.entries(outputParameters).forEach(([key, value]) => {
    if (isEnvironmentParamKey(key)) {
      const { env, key: param } = deserializeEnvironmentParamKey(key);
      if (!environmentParameters[env]) environmentParameters[env] = {};
      environmentParameters[env][param] = extractEnvParamsRecursive(value);
      _.unset(outputParameters, key);
    }
  });

  return {
    parameters: outputParameters,
    environmentParameters: Object.keys(environmentParameters).length
      ? environmentParameters
      : undefined,
  };
}

function extractEnvParamsRecursive(value: any): any {
  if (Array.isArray(value)) {
    return value.map(item => extractEnvParamsRecursive(item));
  }

  if (typeof value === 'object' && value !== null) {
    const cleaned: Record<string, any> = {};

    for (const [k, v] of Object.entries(value)) {
      if (isEnvironmentParamKey(k)) {
        const { key: deserializedKey } = deserializeEnvironmentParamKey(k);
        cleaned[deserializedKey] = extractEnvParamsRecursive(v);
      } else {
        cleaned[k] = extractEnvParamsRecursive(v);
      }
    }

    return cleaned;
  }

  return value;
}
